import { segmentText } from './segmentationRules';
/**
 * Enhanced DOCX Parser with Color Extraction
 * Directly reads DOCX XML to preserve text colors that mammoth.js strips
 *
 * DOCX files are ZIP archives containing XML files:
 * - word/document.xml: Main document content
 * - word/styles.xml: Style definitions
 *
 * Text colors are stored in:
 * - <w:color w:val="FF0000"/> (direct color)
 * - <w:rPr> (run properties) containing <w:color>
 */

import JSZip from 'jszip';

/**
 * Parse DOCX file and extract text with colors preserved
 * @param {File} file - DOCX file
 * @returns {Promise<Array>} Array of segments with sourceHtml containing colors
 */
/**
 * Split text into sentences for CAT segmentation.
 * Handles English (.!?), Indian (।), and missing-space cases (.For -> . For)
 */
export async function parseDocxWithColors(file) {
  const arrayBuffer = await file.arrayBuffer();
  const zip = await JSZip.loadAsync(arrayBuffer);

  // Read document.xml
  const documentXml = await zip.file('word/document.xml')?.async('string');
  if (!documentXml) {
    throw new Error('Invalid DOCX file: missing document.xml');
  }

  // Read styles.xml for style definitions
  const stylesXml = await zip.file('word/styles.xml')?.async('string');
  const styleColors = stylesXml ? parseStyleColors(stylesXml) : {};

  // Parse document and extract paragraphs with formatting
  const segments = parseDocumentXml(documentXml, styleColors);

  return segments;
}

/**
 * Parse style colors from styles.xml
 * @param {string} stylesXml - The styles.xml content
 * @returns {Object} Map of style ID to color
 */
function parseStyleColors(stylesXml) {
  const colors = {};
  const parser = new DOMParser();
  const doc = parser.parseFromString(stylesXml, 'text/xml');

  // Find all style definitions
  const styles = doc.getElementsByTagName('w:style');

  for (const style of styles) {
    const styleId = style.getAttribute('w:styleId');
    if (!styleId) continue;

    // Look for color in run properties
    const colorEl = style.getElementsByTagName('w:color')[0];
    if (colorEl) {
      const colorVal = colorEl.getAttribute('w:val');
      if (colorVal && colorVal !== 'auto' && colorVal !== '000000') {
        colors[styleId] = '#' + colorVal;
      }
    }
  }

  return colors;
}

/**
 * Parse document.xml and extract paragraphs with colors
 * @param {string} documentXml - The document.xml content
 * @param {Object} styleColors - Map of style ID to color
 * @returns {Array} Array of segment objects
 */
function parseDocumentXml(documentXml, styleColors) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(documentXml, 'text/xml');

  const segments = [];
  let segmentId = 1;

  // Find all paragraphs
  const paragraphs = doc.getElementsByTagName('w:p');

  for (const para of paragraphs) {
    const { text, html, hasFormatting, dominantFormatting } = parseParagraph(para, styleColors);
    const cleanText = text.trim();

    if (!cleanText) continue;

    // Smart segmentation using unified rules from segmentationRules.js
    const subSegs = segmentText(cleanText);
    if (subSegs.length > 1) {
      const parentId = segmentId;
      subSegs.forEach((sentence, subIdx) => {
        // Inherit parent paragraph's dominant formatting so split segments preserve style
        const sentenceHtml = (hasFormatting && Object.keys(dominantFormatting).length > 0)
          ? buildFormattedHtml(sentence, dominantFormatting)
          : sentence;
        segments.push({
          id: segmentId++,
          source: sentence,
          sourceHtml: sentenceHtml,
          target: '',
          status: 'empty',
          hasColorFormatting: hasFormatting,
          parent_paragraph_id: parentId,
          sub_index: subIdx,
          total_sub_segments: subSegs.length,
          is_split: true
        });
      });
    } else {
      segments.push({
        id: segmentId++,
        source: cleanText,
        sourceHtml: html,
        target: '',
        status: 'empty',
        hasColorFormatting: hasFormatting
      });
    }
  }

  return segments;
}

/**
 * Parse a single paragraph element
 * @param {Element} para - The w:p element
 * @param {Object} styleColors - Map of style ID to color
 * @returns {Object} { text, html, hasFormatting, dominantFormatting }
 */
function parseParagraph(para, styleColors) {
  const runs = para.getElementsByTagName('w:r');
  let text = '';
  let html = '';
  let hasFormatting = false;
  let dominantFormatting = {};

  for (const run of runs) {
    const { runText, runHtml, hasFormat, runFormatting } = parseRun(run, styleColors);
    text += runText;
    html += runHtml;
    if (hasFormat) {
      hasFormatting = true;
      // Use first formatted run as dominant paragraph formatting
      if (Object.keys(dominantFormatting).length === 0) {
        dominantFormatting = runFormatting;
      }
    }
  }

  return { text, html, hasFormatting, dominantFormatting };
}

/**
 * Parse a single run element (w:r)
 * @param {Element} run - The w:r element
 * @param {Object} styleColors - Map of style ID to color
 * @returns {Object} { runText, runHtml, hasFormat, runFormatting }
 */
function parseRun(run, styleColors) {
  // Get text content
  const textNodes = run.getElementsByTagName('w:t');
  let runText = '';
  for (const t of textNodes) {
    runText += t.textContent || '';
  }

  if (!runText) {
    return { runText: '', runHtml: '', hasFormat: false, runFormatting: {} };
  }

  // Get run properties
  const rPr = run.getElementsByTagName('w:rPr')[0];
  const formatting = extractRunFormatting(rPr, styleColors);

  // Build HTML with formatting
  const runHtml = buildFormattedHtml(runText, formatting);

  return {
    runText,
    runHtml,
    hasFormat: Object.keys(formatting).length > 0,
    runFormatting: formatting
  };
}

/**
 * Extract formatting from run properties (w:rPr)
 * @param {Element} rPr - The w:rPr element
 * @param {Object} styleColors - Map of style ID to color
 * @returns {Object} Formatting properties
 */
function extractRunFormatting(rPr, styleColors) {
  const formatting = {};

  if (!rPr) return formatting;

  // Check for color
  const colorEl = rPr.getElementsByTagName('w:color')[0];
  if (colorEl) {
    const colorVal = colorEl.getAttribute('w:val');
    if (colorVal && colorVal.toLowerCase() !== 'auto' && colorVal !== '000000') {
      formatting.color = '#' + colorVal.toUpperCase();
    }
  }

  // Check for highlight/shading
  const highlightEl = rPr.getElementsByTagName('w:highlight')[0];
  if (highlightEl) {
    const highlightVal = highlightEl.getAttribute('w:val');
    if (highlightVal && highlightVal !== 'none') {
      formatting.backgroundColor = wordColorToHex(highlightVal);
    }
  }

  // Check for shading (another way Word stores background color)
  const shadingEl = rPr.getElementsByTagName('w:shd')[0];
  if (shadingEl) {
    const fillVal = shadingEl.getAttribute('w:fill');
    if (fillVal && fillVal !== 'auto' && fillVal !== 'FFFFFF') {
      formatting.backgroundColor = '#' + fillVal;
    }
  }

  // Check for bold
  const boldEl = rPr.getElementsByTagName('w:b')[0];
  if (boldEl) {
    const boldVal = boldEl.getAttribute('w:val');
    if (boldVal !== '0' && boldVal !== 'false') {
      formatting.bold = true;
    }
  }

  // Check for italic
  const italicEl = rPr.getElementsByTagName('w:i')[0];
  if (italicEl) {
    const italicVal = italicEl.getAttribute('w:val');
    if (italicVal !== '0' && italicVal !== 'false') {
      formatting.italic = true;
    }
  }

  // Check for underline
  const underlineEl = rPr.getElementsByTagName('w:u')[0];
  if (underlineEl) {
    const underlineVal = underlineEl.getAttribute('w:val');
    if (underlineVal && underlineVal !== 'none') {
      formatting.underline = true;
    }
  }

  // Check for strikethrough
  const strikeEl = rPr.getElementsByTagName('w:strike')[0];
  if (strikeEl) {
    formatting.strike = true;
  }

  // Check for font size (w:sz is in half-points, divide by 2 for pt)
  const szEl = rPr.getElementsByTagName('w:sz')[0];
  if (szEl) {
    const szVal = szEl.getAttribute('w:val');
    if (szVal) {
      const pt = parseInt(szVal, 10) / 2;
      if (pt > 0) formatting.fontSize = pt;
    }
  }

  // Check for font name (w:rFonts)
  const rFontsEl = rPr.getElementsByTagName('w:rFonts')[0];
  if (rFontsEl) {
    const fontName = rFontsEl.getAttribute('w:ascii')
      || rFontsEl.getAttribute('w:hAnsi')
      || rFontsEl.getAttribute('w:eastAsia');
    if (fontName) formatting.fontName = fontName;
  }

  // Check for style reference
  const rStyleEl = rPr.getElementsByTagName('w:rStyle')[0];
  if (rStyleEl) {
    const styleId = rStyleEl.getAttribute('w:val');
    if (styleId && styleColors[styleId]) {
      formatting.color = formatting.color || styleColors[styleId];
    }
  }

  return formatting;
}

/**
 * Convert Word color names to hex
 * @param {string} colorName - Word color name (e.g., 'yellow', 'red')
 * @returns {string} Hex color
 */
function wordColorToHex(colorName) {
  const colorMap = {
    'yellow': '#FFFF00',
    'green': '#00FF00',
    'cyan': '#00FFFF',
    'magenta': '#FF00FF',
    'blue': '#0000FF',
    'red': '#FF0000',
    'darkBlue': '#000080',
    'darkCyan': '#008080',
    'darkGreen': '#008000',
    'darkMagenta': '#800080',
    'darkRed': '#800000',
    'darkYellow': '#808000',
    'darkGray': '#808080',
    'lightGray': '#C0C0C0',
    'black': '#000000',
    'white': '#FFFFFF'
  };

  return colorMap[colorName] || colorName;
}

/**
 * Build HTML string with formatting applied
 * @param {string} text - Plain text
 * @param {Object} formatting - Formatting properties
 * @returns {string} HTML with formatting
 */
function buildFormattedHtml(text, formatting) {
  if (!text) return '';

  // Escape HTML entities
  let escapedText = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

  // If no formatting, return plain text
  if (Object.keys(formatting).length === 0) {
    return escapedText;
  }

  // Build style string
  const styles = [];

  if (formatting.color) {
    styles.push(`color: ${formatting.color}`);
  }
  if (formatting.backgroundColor) {
    styles.push(`background-color: ${formatting.backgroundColor}`);
  }
  if (formatting.fontSize) {
    styles.push(`font-size: ${formatting.fontSize}pt`);
  }
  if (formatting.fontName) {
    styles.push(`font-family: ${formatting.fontName}`);
  }

  let html = escapedText;

  // Wrap with style span if needed
  if (styles.length > 0) {
    html = `<span style="${styles.join('; ')}">${html}</span>`;
  }

  // Apply inline tags
  if (formatting.bold) html = `<strong>${html}</strong>`;
  if (formatting.italic) html = `<em>${html}</em>`;
  if (formatting.underline) html = `<u>${html}</u>`;
  if (formatting.strike) html = `<s>${html}</s>`;

  return html;
}

export default { parseDocxWithColors };
